import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  CardTab,
  CustomButton,
  CustomModal,
  CustomSingleSelect,
} from "@ippon/shared-components";
import AddIcon from "@mui/icons-material/Add";
import { AREA_TYPE_LABELS, type AreaType } from "@ippon/shared-types";
import type { AppDispatch, RootState } from "@/store";
import { fetchUnitsByProperty, deleteUnit } from "@/store/unitSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";
import UnitListTable from "../Unit/UnitListTable";
import UnitCreateModal from "../Unit/UnitCreateModal";
import PrivateAreaDetailDrawer from "../Budget/PrivateAreaDetailDrawer";

/**
 * 物件構成タブ — PropertySection / Equipment 統合後の Unit 一覧。
 * area_type 別の CardTab + Unit 一覧テーブル + 新規追加ボタン + 一括削除。
 */

const TAB_KEYS: Array<"all" | AreaType> = [
  "all",
  "private",
  "common",
  "facility",
  "land",
  "other",
];

const TAB_LABEL: Record<"all" | AreaType, string> = {
  all: "すべて",
  ...AREA_TYPE_LABELS,
};

interface Props {
  data?: { id?: string; name?: string };
}

type PerPageForm = { perPage: number };

const PER_PAGE_OPTIONS = [
  { value: 10, label: "10件" },
  { value: 25, label: "25件" },
  { value: 50, label: "50件" },
  { value: 100, label: "100件" },
];

const PropertyComposition: React.FC<Props> = ({ data }) => {
  const propertyId = data?.id;
  const propertyName = data?.name;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { data: units, loading } = useSelector((s: RootState) => s.units.list);

  const [activeTab, setActiveTab] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // 名称クリックは直接遷移せず、まず区画プレビュードロワーを開く (展開で単独ページへ)
  const [unitPreview, setUnitPreview] = useState<{
    id: string;
    name?: string;
  } | null>(null);

  useEffect(() => {
    if (propertyId)
      void dispatch(fetchUnitsByProperty({ property_id: propertyId }));
  }, [propertyId, dispatch]);

  const activeKey = TAB_KEYS[activeTab];
  const activeAreaType: AreaType | undefined =
    activeKey === "all" ? undefined : activeKey;

  const filtered = useMemo(() => {
    if (activeAreaType) {
      return units.filter((u) => u.area_type === activeAreaType);
    }
    return units;
  }, [units, activeAreaType]);

  // タブ切替時に選択をクリア (別タブの id を引きずらない)
  useEffect(() => {
    setSelectedIds([]);
  }, [activeTab]);

  const counts = useMemo(() => {
    const c: Record<"all" | AreaType, number> = {
      all: units.length,
      private: 0,
      common: 0,
      facility: 0,
      land: 0,
      other: 0,
    };
    for (const u of units) c[u.area_type]++;
    return c;
  }, [units]);

  if (!propertyId) return null;

  const tabs = TAB_KEYS.map((k) => ({
    key: k,
    label: TAB_LABEL[k],
    count: counts[k],
    hasNotification: false,
  }));

  const tabData = Object.fromEntries(
    TAB_KEYS.map((k) => [
      k,
      { data: [], pagination: { total: 0, page: 1, limit: 50 } },
    ]),
  );

  // 物件構成の登録ボタンは全て「新規登録」に統一。
  const addLabel = "新規登録";

  const handleOpenDelete = () => {
    if (selectedIds.length === 0) {
      showToast({
        type: "warning",
        message: "削除する区画を選択してください",
      });
      return;
    }
    setOpenDeleteModal(true);
  };

  const handleDelete = async () => {
    setOpenDeleteModal(false);
    setIsDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) => dispatch(deleteUnit({ unit_id: id })).unwrap()),
      );
      showToast({
        type: "success",
        message: `${selectedIds.length}件の区画を削除しました`,
      });
      setSelectedIds([]);
      await dispatch(fetchUnitsByProperty({ property_id: propertyId }));
    } catch (error) {
      console.error("削除エラー:", error);
      showToast({ type: "error", message: "削除に失敗しました" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box>
      <CardTab
        tabs={tabs}
        tabData={tabData}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        loading={loading}
        loadingDelayMs={150}
      >
        {/* CardTab → ツールバー → テーブル の縦間隔は各 8px */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.25,
            py: "8px",
          }}
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#3e3e3e",
                whiteSpace: "nowrap",
              }}
            >
              表示件数
            </Typography>
            <Box sx={{ minWidth: 70 }}>
              <CustomSingleSelect
                label=""
                name="perPage"
                control={perPageControl}
                errors={{}}
                options={PER_PAGE_OPTIONS}
                inputWidth="80px"
                inputWidthSp="100%"
                hasSpacing={false}
                onChangeValue={(value) => {
                  const next = Number(value);
                  if (Number.isFinite(next)) setPerPage(next);
                }}
              />
            </Box>
          </Stack>
          <CustomButton
            label={addLabel}
            onClick={() => setCreateOpen(true)}
            buttonCategory="submit"
            startIcon={<AddIcon sx={{ fontSize: 20 }} />}
          />
          <Can action="unit:delete">
            <CustomButton
              label="削除"
              buttonCategory="delete"
              onClick={handleOpenDelete}
              disabled={isDeleting || selectedIds.length === 0}
              spIcon
            />
          </Can>
        </Box>
        <UnitListTable
          key={`${activeTab}-${perPage}`}
          units={filtered}
          loading={loading}
          onRowClick={(u) =>
            // 直接遷移せず区画プレビュードロワーを開く (展開で単独ページへ)
            setUnitPreview({ id: u.unit_id, name: u.landmark_name })
          }
          enableCheckboxSelection
          selectedIds={selectedIds}
          onRowSelection={setSelectedIds}
          pageSize={perPage}
        />
      </CardTab>

      <UnitCreateModal
        open={createOpen}
        propertyId={propertyId}
        defaultAreaType={activeAreaType}
        onClose={() => setCreateOpen(false)}
        onCreated={(unitId, landmarkName) => {
          void dispatch(fetchUnitsByProperty({ property_id: propertyId }));
          // 登録後はそのまま詳細ページへ (パンくず・タイトル用に landmark_name も渡す)
          navigate(`/properties/${propertyId}/units/${unitId}`, {
            state: {
              propName: propertyName,
              customerName: landmarkName,
              privateAreaName: landmarkName,
              fromSource: "composition",
            },
          });
        }}
      />

      {/* 名称クリックで開く区画プレビュードロワー (展開で単独ページへ) */}
      <PrivateAreaDetailDrawer
        open={!!unitPreview}
        onClose={() => setUnitPreview(null)}
        propertyId={propertyId}
        privateAreaId={unitPreview?.id ?? null}
        expandState={
          unitPreview
            ? {
                propName: propertyName,
                customerName: unitPreview.name,
                privateAreaName: unitPreview.name,
                fromSource: "composition",
              }
            : undefined
        }
      />

      {/* 削除確認モーダル — 他テーブルと同様 deleteModal タイプ */}
      <CustomModal
        title="削除の確認"
        openModal={openDeleteModal}
        handleCloseModal={() => setOpenDeleteModal(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedIds.length}件の区画を削除してもよろしいでしょうか？
            <br />
            削除されたデータは復元できません。
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} justifyContent="center">
          <CustomButton
            label="戻る"
            onClick={() => setOpenDeleteModal(false)}
            buttonCategory="cancelModal"
          />
          <CustomButton
            label="削除"
            onClick={handleDelete}
            buttonCategory="deleteModal"
            disabled={isDeleting}
          />
        </Stack>
      </CustomModal>
    </Box>
  );
};

export default PropertyComposition;
